import React, { useMemo } from 'react';

import type { SignatureFieldEntry } from '@aline/common';
import { Queries } from '@aline/common';
import { link, params } from '@aline/routes';
import { IconButton, Paper, Typography } from '@mui/material';
import { SquarePen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineAvatar, AlineTooltip, defaultEditorPopoverPaperStyles } from 'src/components';
import { Spacer } from 'src/components/generic';
import { CompletedFieldPopoverContent } from 'src/editor/popovers/fields/CompletedFieldPopoverContent';
import { useGetFieldDetails } from 'src/editor/popovers/fields/helpers';
import { SignHerePopoverContent } from 'src/editor/popovers/fields/SignHerePopoverContent';
import { getIsSignatureFieldFormField } from 'src/editor/toolbars';
import { getPathWithDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { Apollo } from 'src/gql/generated';
import { getAssigneeDisplayDetails, useDocumentParams } from 'src/helpers';
import { useGetEditorUserState } from 'src/hooks';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { useCurrentUserPermissionRole } from 'src/store/document/selectors';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  icon: {
    height: 18,
    width: 18,
  },
  wrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
  },
}));

interface SignatureFieldPopoverContentProps {
  signatureFieldEntry: SignatureFieldEntry;
}

export function SignatureFieldPopoverContent({
  signatureFieldEntry,
}: SignatureFieldPopoverContentProps): JSX.Element | null {
  const [signatureField] = signatureFieldEntry;
  const navigate = useNavigate();
  const { documentId } = useDocumentParams();
  const { openDrawer } = useGlobalDrawer();
  const { isInSigningCeremony } = useGetEditorUserState();
  const {
    disabledReason,
    document,
    field,
    showCompletedPopover: showCompletedPopper,
  } = useGetFieldDetails(signatureField.fieldId);
  const fieldRecipientDeclined =
    field?.__typename === 'SignatureField' &&
    field.action?.status === Apollo.WorkflowRecipientStatus.Declined;
  const assignee = field?.action?.assignee;
  const assigneeRole = field?.action?.assigneeRole;
  const { canEdit } = useCurrentUserPermissionRole();
  const { classes } = useStyles();
  const { imgWidthPx } = Queries.getSignatureImageDimensions(signatureField);
  const { avatarColor, primary: assigneeLabel } = getAssigneeDisplayDetails(assignee, assigneeRole);

  const content = useMemo(() => {
    if (
      field?.__typename !== 'SignatureField' ||
      !getIsSignatureFieldFormField(field) ||
      document?.isCertificate
    )
      return null;

    if (showCompletedPopper) return <CompletedFieldPopoverContent inputField={field} />;

    if (isInSigningCeremony)
      return (
        <SignHerePopoverContent
          declined={fieldRecipientDeclined}
          disabledReason={disabledReason}
          inputField={field}
          signatureFieldEntry={signatureFieldEntry}
        />
      );

    return (
      <div className={classes.wrapper}>
        <AlineAvatar
          avatarInfo={assignee || assigneeRole}
          styles={{
            backgroundColor: avatarColor,
          }}
        />

        <Typography noWrap>{assigneeLabel}</Typography>

        <Spacer />

        <AlineTooltip arrow placement="top" title="Edit signature field">
          <div>
            <IconButton
              disabled={!canEdit}
              size="small"
              onClick={() => {
                if (!field?.id) return;
                navigate(
                  getPathWithDrawerSearchParam(
                    link.DocumentDetailFields({ documentId }),
                    window.location.search,
                    params.rightPanel.editFieldId,
                    field.id
                  ),
                  { replace: true }
                );
                openDrawer();
              }}
            >
              <SquarePen className={classes.icon} />
            </IconButton>
          </div>
        </AlineTooltip>
      </div>
    );
  }, [
    assignee,
    assigneeLabel,
    assigneeRole,
    avatarColor,
    canEdit,
    classes.icon,
    classes.wrapper,
    disabledReason,
    document?.isCertificate,
    documentId,
    field,
    fieldRecipientDeclined,
    isInSigningCeremony,
    navigate,
    openDrawer,
    showCompletedPopper,
    signatureFieldEntry,
  ]);

  if (!content) return null;
  return <Paper style={{ ...defaultEditorPopoverPaperStyles, width: imgWidthPx }}>{content}</Paper>;
}
